import type { RecordStatus } from '@rdm/db'

import { AuthUser } from '../common.js'

type CanEditCollaborator = {
  isOwner: boolean
  isLead: boolean
  userId?: number | null
  user?: { id: number } | null
}

/**
 * Single source of truth for which collaborators may modify a record. Used by
 * the API permission checks and by the frontend to decide whether to show edit
 * controls — keep both in sync by only ever changing this function.
 *
 * Deliberately excludes the admin-record-management bypass: that is an
 * API-only concern (see canEditRecord in apps/api) so admins don't see edit
 * controls on records they are not an owner/lead of.
 *
 * Accepts both collaborator shapes: the API's `RecordFull` collaborators
 * (matched on `userId`) and the frontend's `RecordDetailsResult`
 * collaborators (matched on `user.id`).
 */
export const canEditRecordAsCollaborator = (
  user: Pick<AuthUser, 'id'>,
  record: { status: RecordStatus; collaborators: CanEditCollaborator[] },
) => {
  const collaborator = record.collaborators.find((c) => (c.userId ?? c.user?.id) === user.id)

  if (record.status === 'DRAFT') {
    return !!collaborator?.isOwner
  } else if (record.status === 'PENDING_LEAD_APPROVAL') {
    return !!collaborator?.isLead
  } else if (record.status === 'ACTIVE') {
    return !!collaborator?.isOwner || !!collaborator?.isLead
  }

  return false
}
